/**
 * Overview tab (spec §8) — drone snapshot, live MQTT-driven.
 */
import type { DroneLive } from '@/lib/store/drones';
import {
  aaClass,
  batteryClass,
  gcsClass,
  linkClass,
  opsClass,
  satsClass,
  statusClass,
} from '@/lib/store/drones';
import { Badge } from '@/components/ui/badge';
import { rtmStatusTone } from '@/lib/oi/rtmStatus';
import { Dot } from '@/components/ui/dot';
import { Section, SectionGroup } from '@/components/Section';
import { StatusTile } from '@/components/StatusTile';
import { MetricCell, MetricRow2, MetricRow3 } from '@/components/MetricCell';
import { InfoRow } from '@/components/InfoRow';
import { formatAccuracyM, formatPercent, formatUtc } from '@/lib/format';
import { ownTenantId } from '@/lib/tenancy';
import type { C2Link } from '@/lib/mqtt/schemas/c2links';
import type { BadgeClass } from '@/lib/store/drones';
import { cn } from '@/lib/utils';
import { useCustodianDrone, useCustodianHealth } from '@/lib/api/queries';
import { aircraftAgentFromCustodian, gcsAgentFromCustodian } from '@/lib/custodianAgents';
import { droneContextFlags } from '@/lib/droneContextFlags';
import { makeModelLabel } from '@/lib/droneLabel';

export function Overview({ drone }: { drone: DroneLive }) {
  const t = drone.telemetry;
  const c2 = drone.c2;
  const oi = drone.oi;
  // ENG-271: what is hidden, and the Aircraft table's Custodian rows, are
  // described in lib/droneContextFlags.ts. Own-fleet aircraft only: partner and
  // Network ID aircraft are not in our Custodian inventory.
  const f = droneContextFlags.overview;
  const own = drone.origin === 'internal' ? drone.id : null;
  const { data: cDrone } = useCustodianDrone(own);
  const { data: cHealth } = useCustodianHealth(own);
  const agent = aircraftAgentFromCustodian(cDrone, cHealth, t.pipeline_delay_s);
  const gcs = gcsAgentFromCustodian(cDrone, cHealth);
  const makeModel = makeModelLabel(t.drone_model, cDrone);
  const aircraftRows: Array<[string, string | null, boolean?]> = [
    ['Callsign', t.callsign, true],
    ['Serial', drone.id, true],
    ['Make / Model', makeModel],
    ['Type', cDrone?.drone_type || t.drone_type || null],
    ['Status', cDrone?.status ?? null],
    ['Aircraft agent', agent ? agent.status : null],
    ['GCS plugin', gcs && gcs.status !== 'Not configured' ? `${gcs.name.replace('GCS Agent — ', '')} · ${gcs.status}` : null],
    ['Last telemetry', cHealth?.last_telemetry_at ? formatUtc(cHealth.last_telemetry_at) : null],
    ['Flight Mode', t.flight_mode ?? '—', true],
    ['Flight Time', f.flightTime ? (t.flight_time ?? '—') : null],
    ['Battery', f.battery ? formatPercent(t.battery_pct) : null],
    ['AGL', t.agl != null ? `${Math.round(t.agl)} ft${t.aglM != null ? ` · ${Math.round(t.aglM)} m` : ''}` : '—', true],
    ['ATL', f.atl ? (t.atl != null ? `${Math.round(t.atl)} ft` : '—') : null, true],
    [
      'ASL',
      t.asl != null ? `${Math.round(t.asl * 3.28084).toLocaleString()} ft · ${Math.round(t.asl)} m` : '—',
      true,
    ],
  ];

  return (
    <SectionGroup>
      {f.statusTiles && (
        <>
          {/* Status badges row 1 */}
          <MetricRow2>
            <StatusTile label="Ops Status" value={t.ops_status ?? '—'} tone={opsClass(t.ops_status)} />
            <StatusTile
              label="Surveillance"
              value={t.status_label ?? t.status ?? '—'}
              tone={statusClass(t.status)}
            />
          </MetricRow2>

          {/* Status badges row 2 */}
          <MetricRow2>
            <StatusTile
              label="Aircraft Agent"
              value={c2?.aa_agent_status ?? '—'}
              tone={aaClass(c2?.aa_agent_status ?? null, c2?.aa_latency_ms ?? null)}
              sub={
                c2?.aa_producer_id
                  ? `producer:${c2.aa_producer_id}`
                  : c2?.aa_latency_ms != null
                    ? `${c2.aa_latency_ms} ms`
                    : null
              }
            />
            <StatusTile
              label="GCS Agent"
              value={c2?.gcs_agent_status ?? '—'}
              tone={gcsClass(c2?.gcs_agent_status ?? null, c2?.gcs_latency_ms ?? null)}
              sub={
                c2?.gcs_producer_id
                  ? `producer:${c2.gcs_producer_id}`
                  : c2?.gcs_latency_ms != null
                    ? `${c2.gcs_latency_ms} ms`
                    : null
              }
            />
          </MetricRow2>
        </>
      )}

      {/* ENG-271: key/value table (situational awareness) instead of cards. Rows
          with no value are left out rather than shown as a dash. */}
      <Section title="Aircraft">
        <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
          {aircraftRows
            .filter(([, v]) => v != null)
            .map(([label, value, mono]) => (
              <InfoRow key={label} label={label} value={value} mono={mono} />
            ))}
        </div>
      </Section>

      {f.cardLayout && (
        <Section title="Aircraft">
          <MetricRow3>
            <MetricCell label="Flight Time" value={t.flight_time ?? '—'} />
            <MetricCell
              label="Battery"
              value={formatPercent(t.battery_pct)}
              tone={batteryClass(t.battery_pct)}
              sub={t.battery_v != null ? `${t.battery_v.toFixed(1)} V` : null}
              accent
            />
            <MetricCell label="Flight Mode" value={t.flight_mode ?? '—'} mono accentColor="blue" />
          </MetricRow3>
          <MetricRow3>
            <MetricCell
              label="AGL"
              value={t.agl != null ? `${Math.round(t.agl)} ft` : '—'}
              sub={t.aglM != null ? `${Math.round(t.aglM)} m` : null}
              tone="ok"
            />
            <MetricCell
              label="ATL"
              value={t.atl != null ? `${Math.round(t.atl)} ft` : '—'}
              sub={t.atlM != null ? `${Math.round(t.atlM)} m` : null}
            />
            <MetricCell
              label="ASL"
              value={t.asl != null ? `${Math.round(t.asl * 3.28084).toLocaleString()} ft` : '—'}
              sub={t.asl != null ? `${Math.round(t.asl)} m` : null}
            />
          </MetricRow3>
        </Section>
      )}

      {f.c2Links && (
        <Section title="C2 Links">
          <div className="grid grid-cols-3 gap-1.5">
            {(c2?.links ?? []).slice(0, 3).map((l, i) => (
              <LinkCell key={l.label + i} link={l} />
            ))}
            {Array.from({ length: Math.max(0, 3 - (c2?.links.length ?? 0)) }).map((_, i) => (
              <EmptyLinkCell key={`empty-${i}`} idx={(c2?.links.length ?? 0) + i + 1} />
            ))}
          </div>
        </Section>
      )}

      {f.electronicConspicuity && (
        <Section title="Electronic Conspicuity">
          <div className="grid grid-cols-3 gap-1.5">
            <ECCell
              label="ADS-B Out"
              status={c2?.ec?.adsbOut.status ?? 'N/A'}
              sub={c2?.ec?.adsbOut.freq ?? '—'}
              cs={c2?.ec?.adsbOut.callsign ?? null}
            />
            <ECCell
              label="ADS-B In"
              status={c2?.ec?.adsbIn.status ?? 'N/A'}
              sub={c2?.ec?.adsbIn.freq ?? '—'}
              accent="blue"
            />
            <ECCell
              label="TIS-B"
              status={c2?.ec?.tisb.status ?? 'N/A'}
              sub={c2?.ec?.tisb.towers != null ? `${c2.ec.tisb.towers} towers` : '—'}
              accent="amber"
            />
          </div>
        </Section>
      )}

      {/* ENG-271: shown once real GPS data is linked (the raw GCS stream gives
          satellites); until then HDOP / GPS lock are only guesses from accuracy. */}
      {(f.navigation || t.gps_sats != null) && (
        <Section title="Navigation">
          <MetricRow3>
            <MetricCell
              label="Satellites"
              value={t.gps_sats != null ? String(t.gps_sats) : '—'}
              tone={satsClass(t.gps_sats, t.gps_hdop)}
              sub={
                t.gps_sats != null
                  ? t.gps_sats >= 10
                    ? 'Good'
                    : t.gps_sats >= 6
                      ? 'Poor'
                      : 'Critical'
                  : null
              }
            />
            <MetricCell
              label="HDOP"
              value={t.gps_hdop != null ? t.gps_hdop.toFixed(1) : '—'}
              tone={(t.gps_hdop ?? 0) > 2 ? 'warn' : undefined}
            />
            <MetricCell
              label="GPS Lock"
              value={t.gps_lock ?? '—'}
              tone={t.gps_lock === 'Acquired' ? 'ok' : t.gps_lock === 'Acquiring' ? 'warn' : 'crit'}
              accent
            />
          </MetricRow3>
        </Section>
      )}

      {/* The two sections the removed External pane used to own. They are the
          only thing origin changes now — everything above and below renders
          identically for own-fleet, partner and Network-ID aircraft. */}
      {drone.origin === 'partner' && (
        <Section title="Partner">
          <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
            <InfoRow label="Source Tenant" value={drone.partnerTenant ?? '—'} />
            <InfoRow label="Receiving Tenant" value={ownTenantId()} />
            <InfoRow label="Stream Topic" value={`v1/output/aircraft/${drone.id}/telemetry`} mono />
          </div>
        </Section>
      )}

      {t.remote_id && (
        <Section title="Remote Identification">
          <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
            <InfoRow label="UAS ID" value={t.remote_id.uas_id} mono />
            {f.operatorId && (
              <InfoRow label="Operator ID" value={t.remote_id.operator_id ?? '—'} mono />
            )}
            <InfoRow label="UA Type" value={t.remote_id.ua_type ?? t.drone_type} />
            <InfoRow
              label="Emergency Status"
              value={t.remote_id.emergency_status ?? '—'}
              valueClassName={
                t.remote_id.emergency_status && t.remote_id.emergency_status !== 'None'
                  ? 'text-(--color-red)'
                  : undefined
              }
            />
            <InfoRow
              label="Horizontal Accuracy"
              value={formatAccuracyM(t.remote_id.horiz_accuracy_m)}
              mono
            />
            <InfoRow
              label="Vertical Accuracy"
              value={formatAccuracyM(t.remote_id.vert_accuracy_m)}
              mono
            />
          </div>
        </Section>
      )}

      <Section title="Operational Intent">
        <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2 space-y-1">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[0.846em] font-bold">{oi?.oi_ref_id ?? '—'}</span>
            {/* The single RTM status badge (ENG-213) — rtm_state, nothing inferred. */}
            <Badge variant={rtmStatusTone(oi?.status ?? 'Unknown')}>
              {oi?.status ?? 'Unknown'}
            </Badge>
          </div>
          <InfoRow label="Start" value={formatUtc(oi?.start_utc)} />
          <InfoRow label="End" value={formatUtc(oi?.end_utc)} />
          <InfoRow label="Pilot" value={oi?.pilot_name ?? '—'} />
          {oi?.mission_name && <InfoRow label="Mission" value={oi.mission_name} />}
          {oi?.mission_location && <InfoRow label="Location" value={oi.mission_location} />}
          <div className="h-px bg-(--color-border-lite) my-1" />
          <InfoRow label="Operation Type" value={oi?.op_type ?? '—'} mono />
          <InfoRow label="Ops Approval" value={oi?.ops_approval ?? '—'} mono />
          {f.legalRule && <InfoRow label="Legal Rule" value={oi?.legal_rule ?? '—'} mono />}
        </div>

        <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2 mt-1.5">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
              Position Prediction · 1 min
            </span>
            {/* The T+60s prediction's own inside/outside reading — the same
                `pred1min.inside` the map diamond and Flight Ops use. Not OI
                status (that is rtm_state only, ENG-213). */}
            <Badge
              variant={t.pred1min?.inside == null ? 'muted' : t.pred1min.inside ? 'ok' : 'crit'}
            >
              {t.pred1min?.inside == null
                ? '—'
                : t.pred1min.inside
                  ? 'Inside Volume'
                  : 'Outside Volume'}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5 text-[0.692em] font-mono text-(--color-text3)">
            <span>◆</span>
            <span>
              {t.pred1min ? `${t.pred1min.lat.toFixed(3)}, ${t.pred1min.lng.toFixed(3)}` : '—'}
            </span>
            <span className="ml-auto">Assessed just now</span>
          </div>
        </div>
      </Section>
    </SectionGroup>
  );
}

/* ---------- private subcomponents (single-use, internal) ---------- */

function LinkCell({ link }: { link: C2Link }) {
  const tone = linkClass(link.status);
  return (
    <div
      className={cn(
        'rounded-md border border-(--color-border) bg-(--color-surface) px-2 py-2 border-t-2',
        tone === 'ok' && 'border-t-(--color-green)',
        tone === 'warn' && 'border-t-(--color-amber)',
        tone === 'crit' && 'border-t-(--color-red)',
      )}
    >
      <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
        {link.label}
      </div>
      <div className="flex items-center gap-1 mt-0.5">
        <Dot variant={tone} size="xs" />
        <span
          className={cn(
            'text-[0.769em] font-bold',
            tone === 'ok' && 'text-(--color-green)',
            tone === 'warn' && 'text-(--color-amber)',
            tone === 'crit' && 'text-(--color-red)',
          )}
        >
          {link.status === 'online'
            ? 'Online'
            : link.status === 'degraded'
              ? 'Degraded'
              : 'Offline'}
        </span>
      </div>
      <div className="text-[0.692em] font-mono text-(--color-text2)">
        {link.latency_ms != null ? `${link.latency_ms} ms` : '—'}
      </div>
      <div className="text-[0.615em] font-mono text-(--color-text3) mt-0.5">{link.tech}</div>
    </div>
  );
}

function EmptyLinkCell({ idx }: { idx: number }) {
  return (
    <div className="rounded-md border border-dashed border-(--color-border) px-2 py-2 opacity-50">
      <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
        Link {idx}
      </div>
      <div className="text-[0.692em] font-mono text-(--color-text3) mt-1">N/A</div>
    </div>
  );
}

function ECCell({
  label,
  status,
  sub,
  cs,
  accent,
}: {
  label: string;
  status: string;
  sub: string;
  cs?: string | null;
  accent?: 'blue' | 'amber';
}) {
  const tone: BadgeClass = status === 'Active' ? 'ok' : status === 'Disabled' ? 'crit' : 'muted';
  return (
    <div
      className={cn(
        'rounded-md border border-(--color-border) bg-(--color-surface) px-2 py-2',
        accent === 'blue'
          ? 'border-t-2 border-t-(--color-blue)'
          : accent === 'amber'
            ? 'border-t-2 border-t-(--color-amber)'
            : 'border-t-2 border-t-(--color-green)',
      )}
    >
      <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
        {label}
      </div>
      <div className="flex items-center gap-1 mt-1">
        <Dot variant={tone} size="xs" />
        <span
          className={cn(
            'text-[0.692em] font-bold',
            tone === 'ok' && 'text-(--color-green)',
            tone === 'crit' && 'text-(--color-red)',
            tone === 'muted' && 'text-(--color-text3)',
          )}
        >
          {status}
        </span>
      </div>
      <div className="text-[0.692em] font-mono text-(--color-text2) mt-0.5">{sub}</div>
      {cs && <div className="text-[0.615em] font-mono text-(--color-text3) mt-0.5">{cs}</div>}
    </div>
  );
}
